// =========================
// StrategyCard
// Renders `strategy` from the analyze-property response:
//   strategy.recommended  → highlighted top option
//   strategy.ranked[]     → ordered alternatives with impact scoring
// Defensive to multiple possible shapes for each strategy item.
// =========================

import React from "react";

function readStrategy(s) {
  if (!s || typeof s !== "object") return null;
  return {
    name: s.name || s.title || s.label || "Unnamed strategy",
    description: s.description || s.detail || s.rationale || "",
    impact:
      typeof s.impact === "number"
        ? s.impact
        : typeof s.impactScore === "number"
          ? s.impactScore
          : typeof s.score === "number"
            ? s.score
            : null,
    delta: s.delta || s.expectedChange || null,
  };
}

function impactColor(impact) {
  if (typeof impact !== "number") return "#9ca3af";
  if (impact >= 0.66) return "#16a34a";
  if (impact >= 0.33) return "#f59e0b";
  return "#dc2626";
}

function fmtImpact(impact) {
  if (typeof impact !== "number" || !isFinite(impact)) return "—";
  if (impact <= 1.0001 && impact >= 0) return `${Math.round(impact * 100)}%`;
  return Math.round(impact).toString();
}

function ImpactBar({ impact }) {
  const pct = typeof impact === "number"
    ? Math.max(0, Math.min(100, impact <= 1 ? impact * 100 : impact))
    : 0;
  return (
    <div style={{ width: "100%", height: 6, background: "#f3f4f6", borderRadius: 3, marginTop: 6 }}>
      <div style={{ width: `${pct}%`, height: "100%", background: impactColor(impact), borderRadius: 3 }} />
    </div>
  );
}

export default function StrategyCard({ strategy }) {
  if (!strategy) {
    return (
      <div style={emptyStyle}>No strategy data available.</div>
    );
  }

  const recommended = readStrategy(strategy.recommended);
  const ranked = Array.isArray(strategy.ranked)
    ? strategy.ranked.map(readStrategy).filter(Boolean)
    : [];

  // De-dupe: if `recommended` is also the top of `ranked`, drop it from the list.
  const alternates = recommended
    ? ranked.filter((r, i) => !(i === 0 && r.name === recommended.name))
    : ranked;

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>Strategy</div>

      {recommended && (
        <div style={{
          marginTop: 12,
          padding: 14,
          borderRadius: 10,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", letterSpacing: 1, textTransform: "uppercase" }}>
              Recommended
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: impactColor(recommended.impact) }}>
              Impact {fmtImpact(recommended.impact)}
            </div>
          </div>
          <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: "#111827" }}>
            {recommended.name}
          </div>
          {recommended.description && (
            <div style={{ marginTop: 6, fontSize: 14, color: "#374151", lineHeight: 1.5 }}>
              {recommended.description}
            </div>
          )}
          <ImpactBar impact={recommended.impact} />
        </div>
      )}

      {alternates.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Alternatives
          </div>
          {alternates.map((s, i) => (
            <div key={i} style={{
              padding: "10px 0",
              borderTop: i === 0 ? "none" : "1px solid #f3f4f6",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontWeight: 600, color: "#111827" }}>{s.name}</div>
                <div style={{ fontSize: 13, color: impactColor(s.impact), fontWeight: 600 }}>
                  {fmtImpact(s.impact)}
                </div>
              </div>
              {s.description && (
                <div style={{ marginTop: 4, fontSize: 13, color: "#6b7280" }}>{s.description}</div>
              )}
              <ImpactBar impact={s.impact} />
            </div>
          ))}
        </div>
      )}

      {!recommended && alternates.length === 0 && (
        <div style={emptyStyle}>No strategy options available for this analysis.</div>
      )}
    </div>
  );
}

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 20,
};
const headerStyle = {
  fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1,
};
const emptyStyle = {
  fontSize: 13, color: "#9ca3af", marginTop: 10,
};
